// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
// eslint-disable-next-line no-restricted-imports
import {jsx} from 'panel';
import {selx} from 'clsx';

import {canUseViewN} from 'common/components/data-table/utils';
import {SvgIconEl} from 'common/components/svg-icon';
import {FlowsSankeyChartWidgetEl} from 'common/report/flows/charts/flows-sankey-chart';
import {FlowsTopPathsChartWidgetEl} from 'common/report/flows/charts/flows-top-paths-chart';
import {FunnelsStepChartEl} from 'common/report/insights/charts/funnels-step-chart/panel';
import {InsightsBarChartEl} from 'common/report/insights/charts/insights-bar-chart/panel';
import {InsightsMetricChartEl} from 'common/report/insights/charts/insights-metric-chart/panel';
import {InsightsLineChartEl} from 'common/report/insights/charts/insights-line-chart/panel';
import {InsightsPieChartEl} from 'common/report/insights/charts/insights-pie-chart/panel';
import {InsightsTableChartEl} from 'common/report/insights/charts/insights-table-chart/panel';
import {RetentionCurveChartEl} from 'common/report/insights/charts/retention-curve-chart/panel';
import {Timezone} from 'common/constants';
import {ChartRootEl} from 'common/charts/root';

import type {Card} from './index';
import styles from './index.module.styl';

const CardHeader = (props: {cmp: Card}) => {
  const cmp = props.cmp;
  return (
    <div key='card-header' sel={selx(styles.cardHeader)}>
      <div>
        <div sel={selx([styles.cardTitle, `.bold`])} attrs={{title: cmp.params.cardTitle}}>
          <div sel={selx(styles.nameContainer)}>
            <div sel={selx(styles.sideIcon)}>
              <SvgIconEl attrs={{icon: `brand-insights`}} />
            </div>
            <p>{cmp.params.cardTitle}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const CardChart = (props: {cmp: Card}) => {
  const cmp = props.cmp;
  const {chartParams} = cmp.params;

  const chartType = cmp.params.chartOptions.type;
  return (
    <div
      sel={selx(styles.cardChart)}
      style={{
        overflow: chartType === cmp.ChartType.Line || chartType === cmp.ChartType.StackedLine ? `hidden` : `auto`,
      }}
    >
      {!cmp.params.reportLoading ? (
        chartType === cmp.ChartType.Metric ? (
          <div sel='.common-metric-container'>
            <InsightsMetricChartEl
              paramsAndResults={{
                params: cmp.getBookmarkParamsWithDashboardOverrides(),
                results: chartParams,
              }}
              dataGroups={cmp.getDataGroups()}
            />
          </div>
        ) : chartType === cmp.ChartType.FunnelSteps ? (
          <FunnelsStepChartEl
            sel={selx(styles.fullHeight)}
            {...{
              bookmarkData: cmp.getFunnelStepsBookmarkData(),
              loaded: true,
              projectDetails: {
                projectId: cmp.params.projectId.toString(),
                projectAsOfDate: cmp.params.projectAsOfDate,
                projectTimeZone: cmp.params.projectTimezone as Timezone,
                utcOffset: cmp.params.utcOffset,
              },
            }}
          />
        ) : chartType === cmp.ChartType.Sankey ? (
          <FlowsSankeyChartWidgetEl attrs={cmp.getFlowSankeyChartAttributes()} props={cmp.getFlowsChartWidgetProps()} />
        ) : chartType === cmp.ChartType.FlowsTopPaths ? (
          <FlowsTopPathsChartWidgetEl attrs={{loaded: true}} props={cmp.getFlowsChartWidgetProps()} />
        ) : chartType === cmp.ChartType.Line || chartType === cmp.ChartType.StackedLine ? (
          <InsightsLineChartEl
            sel='.line-chart'
            loaded
            timezone={cmp.params.projectTimezone}
            utcOffset={cmp.params.utcOffset}
            dataGroups={cmp.getDataGroups()}
            paramsAndResults={{
              params: cmp.getBookmarkParamsWithDashboardOverrides(),
              results: chartParams,
            }}
          />
        ) : chartType === cmp.ChartType.StackedBar || chartType === cmp.ChartType.Bar ? (
          <InsightsBarChartEl
            dataGroups={cmp.getDataGroups()}
            paramsAndResults={{
              params: cmp.getBookmarkParamsWithDashboardOverrides(),
              results: chartParams,
            }}
          />
        ) : chartType === cmp.ChartType.Pie ? (
          <InsightsPieChartEl
            sel='.pie-chart'
            loaded
            paramsAndResults={{
              params: cmp.getBookmarkParamsWithDashboardOverrides(),
              results: chartParams,
            }}
            dataGroups={cmp.getDataGroups()}
          />
        ) : chartType === cmp.ChartType.Table ? (
          <InsightsTableChartEl
            bookmarkParams={cmp.getBookmarkParamsWithDashboardOverrides()}
            results={chartParams}
            dataGroups={cmp.getDataGroups()}
            startScrolledRight
            canUseViewN={canUseViewN()}
          />
        ) : chartType === cmp.ChartType.RetentionLine || cmp.ChartType.RetentionCurve ? (
          <RetentionCurveChartEl {...cmp.getParamsForRetentionCurveChart()} />
        ) : null
      ) : null}
    </div>
  );
};

const ReportCard = (props: {cmp}) => {
  const cmp = props.cmp;
  return (
    <div sel={selx(styles.cardRoot)}>
      <CardHeader cmp={cmp} />
      <div sel={selx(styles.cardReport)}>
        <ChartRootEl>
          <CardChart cmp={cmp} />
        </ChartRootEl>
      </div>
    </div>
  );
};

export function template(this: Card) {
  return <ReportCard cmp={this} />;
}
